import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Star } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import FeedbackForm from '../components/FeedbackForm';

interface Video {
  _id: string;
  youtubeId: string;
  title: string;
  averageRating: number;
}

function HomePage() {
  const { user } = useAuth();
  const [videos, setVideos] = useState<Video[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await fetch('http://instarate.in:5001/api/videos');
      if (!response.ok) {
        throw new Error('Failed to fetch videos');
      }
      const data = await response.json();
      setVideos(data);
    } catch (error) {
      setError('Failed to load videos. Please try again later.');
    }
  };

  const handleRating = async (videoId: string, rating: number) => {
    if (!user) {
      setError('You must be logged in to rate a video.');
      return;
    }

    try {
      const response = await fetch(`http://instarate.in:5001/api/videos/${videoId}/rate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ rating }),
      });

      if (!response.ok) {
        throw new Error('Failed to update rating');
      }

      await fetchVideos();
    } catch (error) {
      setError('Failed to update rating. Please try again.');
    }
  };

  return (
    <div className="space-y-8">
      <section className="text-center">
        <h1 className="text-4xl font-bold mb-4">🎥 YouTube Video Ranker</h1>
        <p className="text-xl mb-8">Discover and rate the best videos on YouTube!</p>
        <Button onClick={() => window.location.href = '/submit'} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded">
          Submit Your Video
        </Button>
      </section>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <section>
        <h2 className="text-2xl font-semibold mb-4">🏆 Top Rated Videos</h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {videos.map((video) => (
            <Card key={video._id}>
              <CardHeader>
                <CardTitle className="text-lg">{video.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="aspect-w-16 aspect-h-9 mb-4">
                  <iframe
                    src={`https://www.youtube.com/embed/${video.youtubeId}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Button
                        key={star}
                        variant="ghost"
                        size="sm"
                        className="p-0 w-8 h-8"
                        onClick={() => handleRating(video._id, star)}
                      >
                        <Star
                          className={`w-6 h-6 ${
                            star <= Math.round(video.averageRating) ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
                          }`}
                        />
                      </Button>
                    ))}
                  </div>
                  <span className="text-sm font-semibold">
                    {video.averageRating.toFixed(1)} / 5
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="text-center">
        <h2 className="text-2xl font-semibold mb-4">🎵 Create Your Playlist</h2>
        <Button onClick={() => {
          const playlist = videos.map(video => video.youtubeId).join(',');
          window.open(`https://www.youtube.com/watch_videos?video_ids=${playlist}`, '_blank');
        }} className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded">
          Play Top Rated Videos
        </Button>
      </section>

      <section className="text-center">
        <h2 className="text-2xl font-semibold mb-4">📣 We Value Your Feedback</h2>
        <Button onClick={() => setShowFeedbackForm(true)} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded">
          Give Feedback
        </Button>
      </section>

      {showFeedbackForm && (
        <FeedbackForm onClose={() => setShowFeedbackForm(false)} />
      )}
    </div>
  );
}

export default HomePage;

